import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { organizationAPI } from '@/services/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Building2, CreditCard, CheckCircle, ArrowRight, ArrowLeft, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const Setup = () => {
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const { user, updateUser } = useAuth();
  const navigate = useNavigate();

  // Organization data
  const [orgName, setOrgName] = useState('');
  const [orgDescription, setOrgDescription] = useState('');

  // Bank account data
  const [accountNumber, setAccountNumber] = useState('');
  const [ifscCode, setIfscCode] = useState('');
  const [accountHolderName, setAccountHolderName] = useState('');
  const [bankName, setBankName] = useState('');

  const handleCreateOrg = async () => {
    if (!orgName.trim()) {
      toast.error('Organization name is required');
      return;
    }

    setLoading(true);
    try {
      const response = await organizationAPI.create({
        name: orgName,
        description: orgDescription
      });
      
      updateUser({ organization_id: response.data.organization_id });
      toast.success('Organization created!');
      setStep(2);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create organization');
    } finally {
      setLoading(false);
    }
  };

  const handleLinkBank = async () => {
    if (!accountNumber || !ifscCode || !accountHolderName || !bankName) {
      toast.error('All fields are required');
      return;
    }

    setLoading(true);
    try {
      await organizationAPI.linkBankAccount({
        account_number: accountNumber,
        ifsc_code: ifscCode,
        account_holder_name: accountHolderName,
        bank_name: bankName
      });
      
      toast.success('Bank account linked successfully!');
      setStep(3);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to link bank account');
    } finally {
      setLoading(false);
    }
  };

  const handleComplete = () => {
    navigate('/admin');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-white flex items-center justify-center p-6">
      <div className="w-full max-w-xl">
        {/* Logo */}
        <div className="flex items-center justify-center gap-3 mb-8">
          <div className="w-10 h-10 rounded-xl bg-blue-900 flex items-center justify-center shadow-lg shadow-blue-900/20">
            <Building2 className="w-6 h-6 text-white" />
          </div>
          <span className="text-xl font-bold text-gray-900 font-['Outfit']">FinOrchest</span>
        </div>

        {/* Progress Steps */}
        <div className="flex items-center justify-center mb-12">
          {[1, 2, 3].map((s) => (
            <React.Fragment key={s}>
              <div className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-medium shadow-sm ${
                step >= s 
                  ? 'bg-blue-900 text-white shadow-blue-900/20' 
                  : 'bg-gray-100 text-gray-400 border border-gray-200'
              }`}>
                {step > s ? <CheckCircle className="w-5 h-5" /> : s}
              </div>
              {s < 3 && (
                <div className={`w-20 h-0.5 ${step > s ? 'bg-blue-900' : 'bg-gray-200'}`} />
              )}
            </React.Fragment>
          ))}
        </div>

        {/* Step Content */}
        <div className="glass-card p-8" data-testid="setup-card">
          {/* Step 1: Create Organization */}
          {step === 1 && (
            <div className="animate-fadeInUp">
              <h2 className="text-2xl font-bold text-gray-900 mb-2 font-['Outfit']">Create Your Organization</h2>
              <p className="text-gray-500 mb-8">Set up your organizational profile to get started</p>

              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="orgName" className="text-gray-600">Organization Name</Label>
                  <Input
                    id="orgName"
                    value={orgName}
                    onChange={(e) => setOrgName(e.target.value)}
                    placeholder="Acme Corporation"
                    className="h-12 bg-white border-gray-300 text-gray-900 placeholder:text-gray-400 focus:border-blue-600 focus:ring-blue-600"
                    data-testid="org-name-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="orgDesc" className="text-gray-600">Description (Optional)</Label>
                  <Input
                    id="orgDesc"
                    value={orgDescription}
                    onChange={(e) => setOrgDescription(e.target.value)}
                    placeholder="Brief description of your organization"
                    className="h-12 bg-white border-gray-300 text-gray-900 placeholder:text-gray-400 focus:border-blue-600 focus:ring-blue-600"
                    data-testid="org-desc-input"
                  />
                </div>

                <Button
                  onClick={handleCreateOrg}
                  className="w-full h-12 bg-blue-900 hover:bg-blue-800 text-white mt-4 shadow-lg shadow-blue-900/20"
                  disabled={loading}
                  data-testid="create-org-btn"
                >
                  {loading ? (
                    <><Loader2 className="w-5 h-5 animate-spin mr-2" /> Creating...</>
                  ) : (
                    <>Continue <ArrowRight className="w-5 h-5 ml-2" /></>
                  )}
                </Button>
              </div>
            </div>
          )}

          {/* Step 2: Link Bank Account */}
          {step === 2 && (
            <div className="animate-fadeInUp">
              <div className="flex items-center gap-3 mb-2">
                <CreditCard className="w-6 h-6 text-blue-600" />
                <h2 className="text-2xl font-bold text-gray-900 font-['Outfit']">Link Bank Account</h2>
              </div>
              <p className="text-gray-500 mb-8">Connect your main organizational bank account</p>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
                <p className="text-blue-800 text-sm">
                  <strong>Simulation Mode:</strong> This is a demo environment. No real bank connection will be made.
                </p>
              </div>

              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="accountNumber" className="text-gray-600">Account Number</Label>
                    <Input
                      id="accountNumber"
                      value={accountNumber}
                      onChange={(e) => setAccountNumber(e.target.value)}
                      placeholder="1234567890"
                      className="h-12 bg-white border-gray-300 text-gray-900 placeholder:text-gray-400 focus:border-blue-600 focus:ring-blue-600 font-mono"
                      data-testid="account-number-input"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="ifscCode" className="text-gray-600">IFSC Code</Label>
                    <Input
                      id="ifscCode"
                      value={ifscCode}
                      onChange={(e) => setIfscCode(e.target.value.toUpperCase())}
                      placeholder="SBIN0001234"
                      className="h-12 bg-white border-gray-300 text-gray-900 placeholder:text-gray-400 focus:border-blue-600 focus:ring-blue-600 font-mono"
                      data-testid="ifsc-input"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="holderName" className="text-gray-600">Account Holder Name</Label>
                  <Input
                    id="holderName"
                    value={accountHolderName}
                    onChange={(e) => setAccountHolderName(e.target.value)}
                    placeholder="Acme Corporation Pvt Ltd"
                    className="h-12 bg-white border-gray-300 text-gray-900 placeholder:text-gray-400 focus:border-blue-600 focus:ring-blue-600"
                    data-testid="holder-name-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="bankName" className="text-gray-600">Bank Name</Label>
                  <Input
                    id="bankName"
                    value={bankName}
                    onChange={(e) => setBankName(e.target.value)}
                    placeholder="State Bank of India"
                    className="h-12 bg-white border-gray-300 text-gray-900 placeholder:text-gray-400 focus:border-blue-600 focus:ring-blue-600"
                    data-testid="bank-name-input"
                  />
                </div>

                <div className="flex gap-3 mt-4">
                  <Button
                    variant="outline"
                    onClick={() => setStep(1)}
                    className="flex-1 h-12 border-gray-300 text-gray-600 hover:bg-gray-50"
                  >
                    <ArrowLeft className="w-5 h-5 mr-2" /> Back
                  </Button>
                  <Button
                    onClick={handleLinkBank}
                    className="flex-1 h-12 bg-blue-900 hover:bg-blue-800 text-white shadow-lg shadow-blue-900/20"
                    disabled={loading}
                    data-testid="link-bank-btn"
                  >
                    {loading ? (
                      <><Loader2 className="w-5 h-5 animate-spin mr-2" /> Linking...</>
                    ) : (
                      <>Link Account <ArrowRight className="w-5 h-5 ml-2" /></>
                    )}
                  </Button>
                </div>
              </div>
            </div>
          )}

          {/* Step 3: Complete */}
          {step === 3 && (
            <div className="animate-fadeInUp text-center py-8">
              <div className="w-20 h-20 rounded-full bg-blue-100 flex items-center justify-center mx-auto mb-6">
                <CheckCircle className="w-10 h-10 text-blue-600" />
              </div>
              
              <h2 className="text-2xl font-bold text-gray-900 mb-2 font-['Outfit']">Setup Complete!</h2>
              <p className="text-gray-500 mb-8">
                Your organization is ready. You can now create departments, set rules, and start managing funds.
              </p>

              <div className="bg-gray-50 rounded-lg p-4 mb-8 text-left border border-gray-200">
                <h4 className="text-sm font-medium text-gray-600 mb-3">Next Steps:</h4>
                <ul className="space-y-2 text-sm text-gray-700">
                  <li className="flex items-center gap-2">
                    <div className="w-1.5 h-1.5 rounded-full bg-blue-600" />
                    Create your first department
                  </li>
                  <li className="flex items-center gap-2">
                    <div className="w-1.5 h-1.5 rounded-full bg-blue-600" />
                    Create department head users
                  </li>
                  <li className="flex items-center gap-2">
                    <div className="w-1.5 h-1.5 rounded-full bg-blue-600" />
                    Set up spending rules
                  </li>
                </ul>
              </div>

              <Button
                onClick={handleComplete}
                className="w-full h-12 bg-blue-900 hover:bg-blue-800 text-white shadow-lg shadow-blue-900/20"
                data-testid="go-to-dashboard-btn"
              >
                Go to Dashboard <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </div>
          )}
        </div>

        {/* Help text */}
        <p className="text-center text-gray-400 text-sm mt-6">
          Need help? Contact support@finorchest.com
        </p>
      </div>
    </div>
  );
};

export default Setup;
